import { useState } from 'react'
import { Link } from 'react-router'
import { Calculator, Sparkles, CheckCircle, TrendingDown, TrendingUp } from 'lucide-react'

// ─── Comprehensive pricing data ─────────────────────────────

interface QuoteService {
  label: string
  unit: string
  priceMin: number
  priceMax: number
  popular?: boolean
}

const CATEGORY_QUOTES: Record<string, QuoteService[]> = {
  plomberie: [
    { label: 'Débouchage canalisation', unit: 'intervention', priceMin: 80, priceMax: 200 },
    { label: 'Remplacement chauffe-eau', unit: 'pièce', priceMin: 600, priceMax: 1500, popular: true },
    { label: 'Réparation fuite', unit: 'intervention', priceMin: 90, priceMax: 250 },
    { label: 'Installation salle de bain', unit: 'm²', priceMin: 800, priceMax: 1800 },
    { label: 'Remplacement robinetterie', unit: 'pièce', priceMin: 80, priceMax: 200 },
  ],
  electricite: [
    { label: 'Mise aux normes tableau', unit: 'forfait', priceMin: 800, priceMax: 2000, popular: true },
    { label: 'Installation prise/interrupteur', unit: 'point', priceMin: 60, priceMax: 120 },
    { label: 'Câblage complet', unit: 'm²', priceMin: 80, priceMax: 150 },
    { label: 'Installation éclairage', unit: 'point', priceMin: 100, priceMax: 250 },
    { label: 'Dépannage électrique', unit: 'intervention', priceMin: 80, priceMax: 200 },
  ],
  peinture: [
    { label: 'Peinture murs', unit: 'm²', priceMin: 20, priceMax: 45, popular: true },
    { label: 'Peinture plafond', unit: 'm²', priceMin: 25, priceMax: 50 },
    { label: 'Peinture façade', unit: 'm²', priceMin: 25, priceMax: 60 },
    { label: 'Papier peint', unit: 'm²', priceMin: 15, priceMax: 40 },
    { label: 'Enduit + peinture', unit: 'm²', priceMin: 35, priceMax: 65 },
  ],
  renovation: [
    { label: 'Rénovation salle de bain', unit: 'm²', priceMin: 800, priceMax: 1500, popular: true },
    { label: 'Rénovation cuisine', unit: 'm²', priceMin: 500, priceMax: 1200 },
    { label: 'Rénovation complète appart', unit: 'm²', priceMin: 600, priceMax: 1500 },
    { label: 'Création cloison', unit: 'ml', priceMin: 40, priceMax: 100 },
    { label: 'Pose parquet', unit: 'm²', priceMin: 25, priceMax: 70 },
  ],
  menuiserie: [
    { label: 'Porte intérieure', unit: 'pièce', priceMin: 200, priceMax: 600, popular: true },
    { label: 'Fenêtre double vitrage', unit: 'pièce', priceMin: 300, priceMax: 800 },
    { label: 'Placard sur mesure', unit: 'ml', priceMin: 300, priceMax: 800 },
    { label: 'Escalier bois', unit: 'forfait', priceMin: 2000, priceMax: 6000 },
  ],
  toiture: [
    { label: 'Réparation toiture', unit: 'm²', priceMin: 40, priceMax: 120, popular: true },
    { label: 'Réfection complète', unit: 'm²', priceMin: 80, priceMax: 200 },
    { label: 'Nettoyage toiture', unit: 'm²', priceMin: 15, priceMax: 30 },
    { label: 'Gouttières', unit: 'ml', priceMin: 20, priceMax: 50 },
  ],
  serrurerie: [
    { label: 'Ouverture de porte', unit: 'intervention', priceMin: 80, priceMax: 200, popular: true },
    { label: 'Changement serrure', unit: 'pièce', priceMin: 100, priceMax: 350 },
    { label: 'Blindage de porte', unit: 'pièce', priceMin: 500, priceMax: 1500 },
    { label: 'Double de clé', unit: 'pièce', priceMin: 10, priceMax: 50 },
  ],
  climatisation: [
    { label: 'Installation clim split', unit: 'pièce', priceMin: 800, priceMax: 2000, popular: true },
    { label: 'Entretien annuel', unit: 'intervention', priceMin: 80, priceMax: 180 },
    { label: 'Installation PAC', unit: 'forfait', priceMin: 5000, priceMax: 15000 },
    { label: 'VMC double flux', unit: 'forfait', priceMin: 3000, priceMax: 7000 },
  ],
  nettoyage: [
    { label: 'Nettoyage appartement', unit: 'm²', priceMin: 3, priceMax: 8, popular: true },
    { label: 'Nettoyage fin de chantier', unit: 'm²', priceMin: 5, priceMax: 15 },
    { label: 'Nettoyage vitrerie', unit: 'm²', priceMin: 4, priceMax: 10 },
    { label: 'Nettoyage moquette', unit: 'm²', priceMin: 3, priceMax: 8 },
  ],
  jardinage: [
    { label: 'Tonte pelouse', unit: 'm²', priceMin: 0.15, priceMax: 0.40, popular: true },
    { label: 'Taille haie', unit: 'ml', priceMin: 5, priceMax: 15 },
    { label: 'Élagage arbre', unit: 'pièce', priceMin: 200, priceMax: 800 },
    { label: 'Création jardin', unit: 'm²', priceMin: 20, priceMax: 60 },
  ],
  demenagement: [
    { label: 'Studio/T1', unit: 'forfait', priceMin: 300, priceMax: 600, popular: true },
    { label: 'T2/T3', unit: 'forfait', priceMin: 500, priceMax: 1200 },
    { label: 'T4+/Maison', unit: 'forfait', priceMin: 1000, priceMax: 3000 },
    { label: 'Piano/objets lourds', unit: 'pièce', priceMin: 150, priceMax: 500 },
  ],
  'carrelage-sol': [
    { label: 'Carrelage sol', unit: 'm²', priceMin: 25, priceMax: 70, popular: true },
    { label: 'Carrelage mural', unit: 'm²', priceMin: 30, priceMax: 80 },
    { label: 'Mosaïque', unit: 'm²', priceMin: 50, priceMax: 120 },
    { label: 'Faïence salle de bain', unit: 'm²', priceMin: 35, priceMax: 90 },
  ],
  isolation: [
    { label: 'Isolation murs intérieurs', unit: 'm²', priceMin: 30, priceMax: 80, popular: true },
    { label: 'ITE (isolation ext.)', unit: 'm²', priceMin: 100, priceMax: 200 },
    { label: 'Isolation combles', unit: 'm²', priceMin: 20, priceMax: 50 },
    { label: 'Isolation plancher', unit: 'm²', priceMin: 25, priceMax: 60 },
  ],
  maconnerie: [
    { label: 'Ravalement façade', unit: 'm²', priceMin: 30, priceMax: 80, popular: true },
    { label: 'Construction mur', unit: 'm²', priceMin: 40, priceMax: 100 },
    { label: 'Dalle béton', unit: 'm²', priceMin: 50, priceMax: 120 },
    { label: 'Terrassement', unit: 'm³', priceMin: 30, priceMax: 60 },
  ],
  piscine: [
    { label: 'Piscine coque', unit: 'forfait', priceMin: 15000, priceMax: 30000, popular: true },
    { label: 'Piscine béton', unit: 'forfait', priceMin: 25000, priceMax: 50000 },
    { label: 'Entretien annuel', unit: 'forfait', priceMin: 600, priceMax: 1500 },
    { label: 'Liner/rénovation', unit: 'm²', priceMin: 20, priceMax: 50 },
  ],
  securite: [
    { label: 'Alarme maison', unit: 'forfait', priceMin: 500, priceMax: 2000, popular: true },
    { label: 'Vidéosurveillance', unit: 'caméra', priceMin: 150, priceMax: 500 },
    { label: 'Interphone/visiophone', unit: 'pièce', priceMin: 200, priceMax: 800 },
    { label: 'Contrôle d\'accès', unit: 'point', priceMin: 300, priceMax: 1000 },
  ],
}

// ─── Instant Quote Widget ────────────────────────────────────

interface InstantQuoteProps {
  categorySlug: string
  categoryName: string
  compact?: boolean
}

export function InstantQuote({ categorySlug, categoryName, compact = false }: InstantQuoteProps) {
  const services = CATEGORY_QUOTES[categorySlug]
  const [selectedIdx, setSelectedIdx] = useState(-1)
  const [quantity, setQuantity] = useState('')
  const qty = parseFloat(quantity) || 0

  if (!services || services.length === 0) return null

  const selected = selectedIdx >= 0 ? services[selectedIdx] : null
  const priceMin = selected ? Math.round(selected.priceMin * (qty || 1)) : 0
  const priceMax = selected ? Math.round(selected.priceMax * (qty || 1)) : 0
  const _priceMid = Math.round((priceMin + priceMax) / 2)

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${compact ? '' : 'shadow-sm'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-blue-600 px-5 py-4 text-white">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          <div>
            <h3 className="text-sm font-bold">Prix instantané — {categoryName}</h3>
            <p className="text-xs text-blue-100">Estimation marché 2026</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Service pills */}
        <div className="flex flex-wrap gap-2">
          {services.map((s, i) => (
            <button
              key={s.label}
              onClick={() => { setSelectedIdx(i); setQuantity('') }}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                selectedIdx === i
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              } ${s.popular && selectedIdx !== i ? 'ring-1 ring-amber-200' : ''}`}
            >
              {s.label}
              {s.popular && selectedIdx !== i && <Sparkles className="ml-1 inline h-3 w-3 text-amber-500" />}
            </button>
          ))}
        </div>

        {/* Quantity + result */}
        {selected && (
          <div className="mt-4 space-y-3 animate-fade-in">
            {selected.unit !== 'forfait' && selected.unit !== 'intervention' && (
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder={`Quantité (${selected.unit})`}
                  className="w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none"
                />
                <span className="text-xs text-slate-400">{selected.unit}</span>
              </div>
            )}

            {/* Price display */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-slate-400">Estimation {qty > 1 ? `pour ${qty} ${selected.unit}` : ''}</p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">
                    {priceMin.toLocaleString('fr-FR')} — {priceMax.toLocaleString('fr-FR')} €
                  </p>
                </div>
              </div>

              {/* Price breakdown bar */}
              <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-400">
                <div className="flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-emerald-500" />
                  <span>{priceMin.toLocaleString('fr-FR')} €</span>
                </div>
                <div className="relative h-2 flex-1 rounded-full bg-gradient-to-r from-emerald-200 via-amber-200 to-red-200">
                  <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow" />
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-red-500" />
                  <span>{priceMax.toLocaleString('fr-FR')} €</span>
                </div>
              </div>

              <p className="mt-2 text-[10px] text-slate-400">TTC, main d'œuvre incluse · prix moyens constatés</p>
            </div>

            {/* CTA */}
            <Link
              to={`/devis-gratuit?service=${categorySlug}`}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-md shadow-primary/20 hover:bg-primary/90 transition-all"
            >
              <CheckCircle className="h-4 w-4" /> Obtenir un prix exact — Gratuit
            </Link>
          </div>
        )}

        {/* Quick prices table when nothing selected */}
        {!selected && (
          <div className="mt-4">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100">
                {services.map((s, i) => (
                  <tr key={s.label} className="cursor-pointer transition-colors hover:bg-slate-50" onClick={() => setSelectedIdx(i)}>
                    <td className="py-2 text-slate-700">{s.label}</td>
                    <td className="py-2 text-right font-semibold text-slate-900">
                      {s.priceMin.toLocaleString('fr-FR')} — {s.priceMax.toLocaleString('fr-FR')} €<span className="font-normal text-slate-400">/{s.unit}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
